import React, { useState, useEffect } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { UserPlus, Loader2, Lightbulb } from 'lucide-react';

const OWNER_OPTIONS = [
  { value: 'front_desk',     label: 'Front Desk (control point)' },
  { value: 'manager_on_duty', label: 'Manager on Duty' },
  { value: 'housekeeping',   label: 'Housekeeping' },
  { value: 'maintenance',    label: 'Maintenance' },
  { value: 'concierge',      label: 'Concierge' },
  { value: 'transport',      label: 'Transport / Driver' },
  { value: 'reservations',   label: 'Reservations' },
];

const ROUTE_TO_OPTIONS = [
  { value: 'front_desk',     label: 'Front Desk' },
  { value: 'housekeeping',   label: 'Housekeeping' },
  { value: 'maintenance',    label: 'Maintenance' },
  { value: 'transport',      label: 'Transport / Driver' },
  { value: 'reservations',   label: 'Reservations' },
  { value: 'concierge',      label: 'Concierge' },
];

const NORMALIZE = (v) => (v ? String(v).toLowerCase() : '');

/**
 * AssignPopover — reassign owner + routing department on a task.
 *
 * Front Desk is the default control point. Staff can override to another
 * department here. Route-to is shown alongside so the routing decision
 * is part of the same assignment moment.
 */
export const AssignPopover = ({ task, trigger, onConfirm }) => {
  const [open, setOpen] = useState(false);
  const [owner, setOwner] = useState(NORMALIZE(task?.owner) || 'front_desk');
  const [routeTo, setRouteTo] = useState(NORMALIZE(task?.route_to) || 'front_desk');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setOwner(NORMALIZE(task?.owner) || 'front_desk');
      setRouteTo(NORMALIZE(task?.route_to) || 'front_desk');
    }
  }, [open, task?.id, task?.owner, task?.route_to]);

  const handleSave = async () => {
    setSubmitting(true);
    try {
      await onConfirm({ assigned_to_user_id: owner, route_to: routeTo });
      setOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild data-testid="assign-popover-trigger">
        {trigger || (
          <Button size="sm" variant="outline" className="text-xs h-7 rounded-full px-3 text-stone-600">
            <UserPlus className="h-3 w-3 mr-1" /> Assign
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-3 space-y-3"
        data-testid="assign-popover-content"
      >
        <div className="flex items-start gap-1.5 text-[11px] text-stone-500">
          <Lightbulb className="h-3.5 w-3.5 text-[#3F617E] mt-0.5 flex-shrink-0" />
          <span>Front Desk owns by default — the routed department handles the work.</span>
        </div>

        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-wide text-stone-500">Owner</Label>
          <Select value={owner} onValueChange={setOwner}>
            <SelectTrigger className="h-8 text-xs" data-testid="assign-owner-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OWNER_OPTIONS.map(o => (
                <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-[11px] uppercase tracking-wide text-stone-500">Route to</Label>
          <Select value={routeTo} onValueChange={setRouteTo}>
            <SelectTrigger className="h-8 text-xs" data-testid="assign-route-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROUTE_TO_OPTIONS.map(r => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            onClick={() => setOpen(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="h-7 px-3 text-xs bg-[#3F617E] hover:bg-[#345470] rounded-full"
            onClick={handleSave}
            disabled={submitting}
            data-testid="assign-save-button"
          >
            {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
            Save
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default AssignPopover;
